import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';

// Admin emails list - should match AuthContext
const ADMIN_EMAILS = ['trionapps@gmail.com'];

/**
 * Check if analytics should be disabled
 * @param {Object} user - Current user object from Firebase Auth
 * @returns {boolean} - True if analytics should be disabled
 */
export const isAnalyticsDisabled = (user = null) => {
  // Disable on localhost
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname.includes('localhost');

  if (isLocalhost) {
    console.log('[Analytics] Disabled: Running on localhost');
    return true;
  }

  // Disable if user is logged in as admin
  if (user && user.email && ADMIN_EMAILS.includes(user.email)) {
    console.log('[Analytics] Disabled: Admin user logged in');
    return true;
  }

  return false;
};

/**
 * Track a custom event with optional parameters
 * @param {string} eventName - Name of the event
 * @param {Object} eventParams - Event parameters
 * @param {Object} user - Current user object (optional)
 */
export const trackEvent = (eventName, eventParams = {}, user = null) => {
  if (isAnalyticsDisabled(user)) {
    return;
  }

  try {
    logEvent(analytics, eventName, {
      ...eventParams,
      timestamp: new Date().toISOString(),
    });
    console.log(`[Analytics] Event tracked: ${eventName}`, eventParams);
  } catch (error) {
    console.error('[Analytics] Error tracking event:', error);
  }
};

/**
 * Track blog post view
 * @param {string} blogTitle - Title of the blog post
 * @param {string} blogCategory - Category of the blog post
 * @param {string} blogId - ID of the blog post
 * @param {Object} user - Current user object
 */
export const trackBlogView = (blogTitle, blogCategory, blogId, user = null) => {
  trackEvent('blog_view', {
    blog_title: blogTitle,
    blog_category: blogCategory,
    blog_id: blogId,
    content_type: 'blog_post',
  }, user);
};

/**
 * Track blog category filter
 * @param {string} category - Selected category
 * @param {Object} user - Current user object
 */
export const trackCategoryFilter = (category, user = null) => {
  trackEvent('category_filter', {
    selected_category: category,
  }, user);
};

/**
 * Track social share
 * @param {string} platform - Social platform (linkedin, twitter, facebook)
 * @param {string} blogTitle - Title of the shared blog
 * @param {Object} user - Current user object
 */
export const trackSocialShare = (platform, blogTitle, user = null) => {
  trackEvent('share', {
    method: platform,
    content_type: 'blog_post',
    item_id: blogTitle,
  }, user);
};

/**
 * Track newsletter subscription
 * @param {string} email - Subscriber email
 * @param {Object} user - Current user object
 */
export const trackNewsletterSubscribe = (email, user = null) => {
  trackEvent('newsletter_subscribe', {
    subscriber_email: email,
  }, user);
};

/**
 * Track search query in blogs
 * @param {string} searchTerm - Search term used
 * @param {number} resultsCount - Number of results found
 * @param {Object} user - Current user object
 */
export const trackBlogSearch = (searchTerm, resultsCount = 0, user = null) => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
  }, user);
};

/**
 * Track page view
 * @param {string} pagePath - Current page path
 * @param {string} pageTitle - Current page title
 * @param {Object} user - Current user object
 */
export const trackPageView = (pagePath, pageTitle, user = null) => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  }, user);
};

/**
 * Track related blog click
 * @param {string} fromBlogTitle - Original blog title
 * @param {string} toBlogTitle - Clicked related blog title
 * @param {Object} user - Current user object
 */
export const trackRelatedBlogClick = (fromBlogTitle, toBlogTitle, user = null) => {
  trackEvent('related_blog_click', {
    from_blog: fromBlogTitle,
    to_blog: toBlogTitle,
  }, user);
};

/**
 * Track time spent on blog post
 * @param {string} blogTitle - Title of the blog post
 * @param {number} timeSpent - Time spent in seconds
 * @param {Object} user - Current user object
 */
export const trackBlogReadTime = (blogTitle, timeSpent, user = null) => {
  trackEvent('blog_read_time', {
    blog_title: blogTitle,
    time_spent_seconds: timeSpent,
    engagement_time: timeSpent,
  }, user);
};

/**
 * Track copy link action
 * @param {string} blogTitle - Title of the blog post
 * @param {Object} user - Current user object
 */
export const trackCopyLink = (blogTitle, user = null) => {
  trackEvent('copy_link', {
    content_type: 'blog_post',
    item_id: blogTitle,
  }, user);
};
